import { GET_DATA_SOURCE_REQUEST, GET_SINK_REQUEST } from 'js/constants/actionTypes';
import { CONNECTION_CATEGORY_FILE } from 'js/constants/connectionCategories';
import UIUtils from 'js/lib/UIUtils';
import useFlowsAdapter from 'new/components/flows/adapter/useFlowsAdapter';
import createFlowService from 'new/components/flows/service/createFlowService';
import useCDC from 'new/components/flows/types/cdc/CreateCDCFlow/CDCFlow/useCDC';
import flowTypes from 'new/components/flows/types/flowTypes';
import useFlowParams from 'new/components/flows/useFlowParams';
import useFlowQueryParams from 'new/components/flows/useFlowQueryParams';
import { useForm } from 'new/elements/Form/useForm';
import usePaging from 'new/state/paging/repository/usePaging';
import useRequests from 'new/state/request/repository/useRequests';
import { useEffect, useMemo, useState } from 'react';

export default function useInitialSetupPage() {
  const { resourceType, id, nexsetId } = useFlowParams();
  const [initialLoading, setInitialLoading] = useState(true);
  const { flowId } = useFlowQueryParams();
  const {
    updateFormData,
    selectedConnection,
    connectionCategory,
    credentialId,
    connectionType,
    source,
    nexset,
    sink,
  } = useCDC();
  const { showError } = useRequests();
  const { setLoaderMessages } = useForm();
  const { requestResources, pollNexset } = useFlowsAdapter();

  const flowService = createFlowService();
  const { getSinkConfigFields } = usePaging();

  useEffect(() => {
    setLoaderMessages({
      [GET_DATA_SOURCE_REQUEST]: 'Loading source',
      [GET_SINK_REQUEST]: 'Loading destination',
    });
  }, []);

  useEffect(() => {
    updateFormData({
      connectionType: !!flowId ? 'SINK' : 'SOURCE',
      flowType: 'cdc',
    });
  }, []);

  const request = useMemo(
    () =>
      pollNexset((foundNexset) => {
        updateFormData({ nexset: foundNexset });
      }),
    [],
  );

  useEffect(() => {
    if (connectionType === 'SINK') {
      // Only poll for the "first" nexset when the user hasn't already chosen
      // one. With a nexsetId prop (multi-RUT attach), the explicit selection
      // is loaded by requestResources — falling back to pollNexset's
      // data_sets[0] would silently override that choice.
      if (source && !nexset && !nexsetId) {
        request(source.origin_node_id);
      }
    }
  }, [source, sink, connectionType, nexset, nexsetId]);

  const setup = async () => {
    setInitialLoading(true);
    let sink, source, nexset;

    try {
      ({ sink, source, nexset } = await requestResources({
        resourceType,
        id,
        flowId,
        nexsetId,
      }));
      const _selectedConnection =
        resourceType === 'sink'
          ? sink?.connector_type || selectedConnection
          : source?.connector_type || selectedConnection;

      const { config, selectedBucket } = flowService.initializeFormToFileCategory(
        sink?.sink_config,
      );

      // Get sink configuration fields for the selected connection
      const sinkConfigFields = getSinkConfigFields(_selectedConnection, flowTypes.cdc);

      // Create initial sinkConfig with proper defaults
      let initialSinkConfig = {
        name: sink?.name,
        description: sink?.description,
        ...(connectionCategory === CONNECTION_CATEGORY_FILE ? config : sink?.sink_config || {}),
      };

      // Apply default values from connector configuration fields
      if (sinkConfigFields && sinkConfigFields.length > 0) {
        try {
          initialSinkConfig = UIUtils.configWithMissingDefaults(
            initialSinkConfig,
            sinkConfigFields,
          );
        } catch (error) {
          console.warn('Error applying default values to sinkConfig:', error);
        }
      }

      // Apply CDC-specific defaults if not already set
      if (!initialSinkConfig['cdc.enabled']) {
        initialSinkConfig['cdc.enabled'] = true;
      }
      if (!initialSinkConfig['cdc.mode']) {
        initialSinkConfig['cdc.mode'] = 'multi_table';
      }

      // Ensure mapping object exists with proper defaults
      if (!initialSinkConfig.mapping) {
        initialSinkConfig.mapping = {
          mode: 'auto',
          tracker_mode: 'NONE',
        };
      } else {
        // Ensure mapping has required properties
        if (!initialSinkConfig.mapping.mode) {
          initialSinkConfig.mapping.mode = 'auto';
        }
        if (!initialSinkConfig.mapping.tracker_mode) {
          initialSinkConfig.mapping.tracker_mode = 'NONE';
        }
      }

      updateFormData({
        source,
        nexset,
        sink,
        resourceConfig: {
          resourceConfig: {},
        },
        sinkConfig: initialSinkConfig,
        selectedBucket,
        selectedConnection: _selectedConnection,
        ...(sink || source
          ? {
              // Prefer the user's current selection (set on the authenticate
              // page) over the resource's original credential — otherwise going
              // back to change the credential and returning would silently
              // revert it on re-mount.
              credentialId:
                resourceType === 'sink'
                  ? credentialId || sink?.data_credentials
                  : credentialId || source?.data_credentials,
            }
          : {}),
      });
    } catch (e) {
      console.error(e);
      showError('There was an error trying to setup the resources');
    }

    setInitialLoading(false);
  };

  useEffect(() => {
    updateFormData({
      connectionType: resourceType.toUpperCase(),
    });
  }, []);

  useEffect(() => {
    setup();

    return () => {
      request.cancel();
    };
  }, []);

  return initialLoading;
}
